import { supabase } from '../../../integrations/supabase/client';

const EXTRACTION_API_URL = import.meta.env.VITE_EXTRACTION_API_URL || 'https://extraction.ciobaa.easypanel.host';
const EXTRACTION_API_SECRET = import.meta.env.VITE_EXTRACTION_API_SECRET || '';

const authHeaders = {
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${EXTRACTION_API_SECRET}`,
};

// --- Types ---

export interface WahaChat {
  id: string;
  name: string;
  phone: string;
  timestamp: number | null;
  lastMessage: string | null;
}

export interface WahaMessageItem {
  id: string;
  body: string;
  timestamp: number;
  fromMe: boolean;
  hasMedia: boolean;
  mediaType: string | null;
  mediaUrl: string | null;
  mimetype: string | null;
}

export interface WahaConnectionInfo {
  sessionName: string;
  status: string;
  numeroTelefono: string;
}

// --- API ---

export const wahaChatsApi = {
  /**
   * Find the WAHA session for a given agent ID.
   * Flow: agent_channels (agent_id → numero_telefono) → whatsapp_connections (numero_telefono → session)
   * Returns null if no WAHA connection exists or it's not connected.
   */
  async getSessionForAgent(agentId: string): Promise<WahaConnectionInfo | null> {
    // Step 1: Find the phone number assigned to this agent
    const { data: channelData, error: channelError } = await supabase
      .from('agent_channels')
      .select('numero_telefono')
      .eq('agent_id', agentId)
      .maybeSingle();

    if (channelError || !channelData?.numero_telefono) return null;

    // Step 2: Find the WAHA session for this phone number
    const { data, error } = await supabase
      .from('whatsapp_connections')
      .select('nombre_instancia, estado, numero_telefono')
      .eq('numero_telefono', channelData.numero_telefono)
      .eq('estado', 'connected')
      .maybeSingle();

    if (error || !data) return null;

    return {
      sessionName: data.nombre_instancia,
      status: data.estado,
      numeroTelefono: data.numero_telefono,
    };
  },

  /**
   * Fetch recent chats from WAHA for a given session.
   */
  async getChats(sessionName: string, limit = 20): Promise<WahaChat[]> {
    const response = await fetch(
      `${EXTRACTION_API_URL}/api/chats/${sessionName}?limit=${limit}`,
      { headers: authHeaders }
    );

    if (!response.ok) {
      throw new Error(`Error fetching WAHA chats: ${response.status}`);
    }

    const data = await response.json();
    return data.chats;
  },

  /**
   * Fetch messages for a specific chat from WAHA.
   */
  async getMessages(
    sessionName: string,
    chatId: string,
    limit = 6,
    offset = 0
  ): Promise<{ messages: WahaMessageItem[]; hasMore: boolean }> {
    const response = await fetch(
      `${EXTRACTION_API_URL}/api/chats/${sessionName}/${encodeURIComponent(chatId)}/messages?limit=${limit}&offset=${offset}`,
      { headers: authHeaders }
    );

    if (!response.ok) {
      throw new Error(`Error fetching WAHA messages: ${response.status}`);
    }

    const data = await response.json();
    return { messages: data.messages, hasMore: data.hasMore };
  },
};
